// v3 avenue, authored the way age-of-mcp writes it: a horse-cart dirt track laid
// as a terrain-warped deck scored by worn wheel ruts, with a gravel kerb on open
// sides. The library has no terrain, so the deck warps to FLAT corners (the same
// look the flattened source baked) — the ground-tile helpers it needs (corners,
// surfaceY, tileTilt, warpedSlab) are inlined here with the terrain coupling
// stripped, since the lib has no terrain module. Rendered standalone, so all
// four sides carry a kerb and no clean ruts are drawn (connections = 0). Ported
// 1:1 from age-of-mcp's v3/avenue.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// --- Inlined ground-tile helpers (flat, terrain coupling stripped) -----------
type Corners = readonly [number, number, number, number];
const FLAT: Corners = [0, 0, 0, 0];
function corners(heights?: readonly number[]): Corners {
  if (!heights || heights.length < 4) return FLAT;
  return [heights[0]!, heights[1]!, heights[2]!, heights[3]!];
}
function surfaceY(lx: number, lz: number, half: number, c: Corners): number {
  const u = (lx / half + 1) / 2;
  const v = (lz / half + 1) / 2;
  const [sw, se, nw, ne] = c;
  return sw * (1 - u) * (1 - v) + se * u * (1 - v) + nw * (1 - u) * v + ne * u * v;
}
function tileTilt(half: number, c: Corners): { tiltX: number; tiltZ: number } {
  const [sw, se, nw, ne] = c;
  const span = 2 * half || 1;
  const slopeX = ((se + ne) - (sw + nw)) / 2 / span;
  const slopeZ = ((nw + ne) - (sw + se)) / 2 / span;
  return { tiltX: -Math.atan(slopeZ), tiltZ: Math.atan(slopeX) };
}
type V = readonly [number, number, number];
function quad(out: number[], p0: V, p1: V, p2: V, p3: V): void {
  out.push(...p0, ...p1, ...p2, ...p0, ...p2, ...p3);
}
function warpedSlab(
  half: number, thickness: number, color: string, c: Corners, lift = 0,
): Part {
  const sw = c[0] + lift, se = c[1] + lift, nw = c[2] + lift, ne = c[3] + lift;
  const yb = Math.min(...c) - thickness;
  const h = half;
  const tSW: V = [-h, sw, -h], tSE: V = [h, se, -h];
  const tNW: V = [-h, nw,  h], tNE: V = [h, ne,  h];
  const bSW: V = [-h, yb, -h], bSE: V = [h, yb, -h];
  const bNW: V = [-h, yb,  h], bNE: V = [h, yb,  h];
  const pos: number[] = [];
  quad(pos, tSW, tNW, tNE, tSE);
  quad(pos, bSW, bSE, bNE, bNW);
  quad(pos, tNW, bNW, bNE, tNE);
  quad(pos, tSE, bSE, bSW, tSW);
  quad(pos, tSE, tNE, bNE, bSE);
  quad(pos, tSW, bSW, bNW, tNW);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  return { geometry, color, position: [0, 0, 0] };
}

// --- Avenue (1:1 from the source) --------------------------------------------
const DIRT    = "#b3a98c";
const ROADBED = "#94896c";
const RUT     = "#857149";
const EDGE    = "#9a9079";
const STONE   = ["#cfc7b4", "#b9b09a", "#dcd4c0"];

const HALF   = 0.5;
const SLAB_H = 0.12;
const LIFT   = 0.025;
const EPS    = 0.004;

const RUT_OFF = 0.15;
const RUT_W   = 0.08;
const EDGE_W  = 0.06;
const KERB_H  = 0.013;

const E = 0, N = 2, W = 4, S = 6;
const DIRS = [E, N, W, S] as const;
const VEC: Record<number, readonly [number, number]> = {
  [E]: [1, 0], [N]: [0, 1], [W]: [-1, 0], [S]: [0, -1],
};

type Surf = { y: (lx: number, lz: number) => number; tilt: { tiltX: number; tiltZ: number } };

function mark(lx: number, lz: number, sx: number, sz: number, color: string, surf: Surf, extra = 1): Part {
  const h = SLAB_H + EPS * extra;
  return {
    geometry: new THREE.BoxGeometry(sx, h, sz),
    color,
    position: [lx, surf.y(lx, lz) + LIFT + EPS * extra - h / 2, lz],
    rotation: [surf.tilt.tiltX, 0, surf.tilt.tiltZ],
    eulerOrder: "ZYX",
  };
}

function straightRuts(vertical: boolean, surf: Surf): Part[] {
  const len = HALF * 2;
  return vertical
    ? [mark(-RUT_OFF, 0, RUT_W, len, RUT, surf), mark(RUT_OFF, 0, RUT_W, len, RUT, surf)]
    : [mark(0, -RUT_OFF, len, RUT_W, RUT, surf), mark(0, RUT_OFF, len, RUT_W, RUT, surf)];
}

function stubRuts(dir: number, surf: Surf): Part[] {
  const [vx, vz] = VEC[dir]!;
  const vertical = vz !== 0;
  const cx = vx * 0.25, cz = vz * 0.25, len = HALF + 0.05;
  return vertical
    ? [mark(cx - RUT_OFF, cz, RUT_W, len, RUT, surf), mark(cx + RUT_OFF, cz, RUT_W, len, RUT, surf)]
    : [mark(cx, cz - RUT_OFF, len, RUT_W, RUT, surf), mark(cx, cz + RUT_OFF, len, RUT_W, RUT, surf)];
}

function curveRuts(a: number, b: number, surf: Surf): Part[] {
  const [ax, az] = VEC[a]!, [bx, bz] = VEC[b]!;
  const jx = (ax + bx) * HALF, jz = (az + bz) * HALF;
  let a0 = Math.atan2(az * HALF - jz, ax * HALF - jx);
  let a1 = Math.atan2(bz * HALF - jz, bx * HALF - jx);
  while (a1 - a0 >  Math.PI) a1 -= 2 * Math.PI;
  while (a1 - a0 < -Math.PI) a1 += 2 * Math.PI;
  const out: Part[] = [];
  const steps = 5;
  for (const rad of [HALF - RUT_OFF, HALF + RUT_OFF]) {
    for (let i = 0; i <= steps; i++) {
      const ang = a0 + (a1 - a0) * (i / steps);
      out.push(mark(jx + Math.cos(ang) * rad, jz + Math.sin(ang) * rad, RUT_W, RUT_W, RUT, surf));
    }
  }
  return out;
}

function wheelRuts(connections: number, surf: Surf): Part[] {
  const has = (bit: number): boolean => (connections & (1 << bit)) !== 0;
  const on = DIRS.filter(has);
  const c = on.length;
  if (c >= 4 || c === 0) return [];
  if (c === 3) return straightRuts(has(N) && has(S), surf);
  if (c === 2) {
    const opposite = Math.abs(on[0]! - on[1]!) === 4;
    return opposite ? straightRuts(has(N) && has(S), surf) : curveRuts(on[0]!, on[1]!, surf);
  }
  return stubRuts(on[0]!, surf);
}

function kerbEdges(connections: number, surf: Surf): Part[] {
  const has = (bit: number): boolean => (connections & (1 << bit)) !== 0;
  const out: Part[] = [];
  const o = HALF - EDGE_W / 2;
  const kerb = (lx: number, lz: number, sx: number, sz: number): Part => {
    const h = SLAB_H + KERB_H;
    return {
      geometry: new THREE.BoxGeometry(sx, h, sz),
      color: EDGE,
      position: [lx, surf.y(lx, lz) + LIFT + KERB_H - h / 2, lz],
      rotation: [surf.tilt.tiltX, 0, surf.tilt.tiltZ],
      eulerOrder: "ZYX",
    };
  };
  if (!has(N)) out.push(kerb(0,  o, HALF * 2, EDGE_W));
  if (!has(S)) out.push(kerb(0, -o, HALF * 2, EDGE_W));
  if (!has(E)) out.push(kerb( o, 0, EDGE_W, HALF * 2));
  if (!has(W)) out.push(kerb(-o, 0, EDGE_W, HALF * 2));
  return out;
}

function stoneFord(surf: Surf): Part[] {
  const out: Part[] = [];
  for (let r = 0; r < 4; r++) {
    const z = -0.33 + r * 0.22;
    for (let i = 0; i < 3; i++) {
      const x = -0.28 + i * 0.28;
      const col = STONE[(r + i) % STONE.length]!;
      out.push(mark(x, z, 0.24, 0.16, col, surf, 1.6));
    }
  }
  return out;
}

// The source's stage(), rendered standalone (connections = 0, no crossing, flat
// heights). The flattened file maps stage 0 -> roadbed, stage 1 -> bare deck,
// and complete -> deck + kerbs (+ ruts, which are empty at connections = 0).
function partsFor(s: number, connections = 0, crosswalk = false, heights?: readonly number[]): Part[] {
  const c: Corners = corners(heights);
  const surf: Surf = {
    y: (lx, lz) => surfaceY(lx, lz, HALF, c),
    tilt: tileTilt(HALF, c),
  };
  if (s <= 0) return [warpedSlab(HALF, 0.1, ROADBED, c, LIFT * 0.6)];

  const deck = warpedSlab(HALF, SLAB_H, DIRT, c, LIFT);
  if (s === 1) return [deck];
  if (s === 2) return [deck, ...kerbEdges(connections, surf)];
  if (crosswalk) return [deck, ...stoneFord(surf)];
  return [deck, ...kerbEdges(connections, surf), ...wheelRuts(connections, surf)];
}

export const avenueV3 = defineModel({
  id: "avenue-v3",
  tiles: { w: 1, h: 1 },
  batchId: "45-optimized",
  name: "Avenue (v3)",
  tags: ["building","avenue","low-poly","age-of-mcp","v3"],
  summary: "Low-poly avenue.",
  description: "A road tile for vehicle traffic.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
